import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Zap, Loader2, X, PlayCircle, Check, AlertTriangle, MessageSquare, Send, Mail, CalendarDays, Megaphone, Building2, Cloud, Database, Users } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";

const ease = [0.16, 1, 0.3, 1];
const ICONS = { discord: MessageSquare, telegram: Send, email: Mail, google_calendar: CalendarDays, meta_ads: Megaphone, microsoft_365: Cloud, google_workspace: Building2, sap: Database, workday: Users };

function ConnectDialog({ item, onClose, onDone, t }) {
  const [config, setConfig] = useState({});
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/integrations/${item.provider}/connect`, { config });
      toast.success(t("os.integ.connectedToast")); onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="integ-connect-dialog">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }} className="card-surface w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-editorial text-2xl">{t("os.integ.connectTitle")} {item.name}</h3>
          <button onClick={onClose} data-testid="integ-connect-close" className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          {item.fields.map((f) => (
            <div key={f.key}>
              <label className="text-sm font-medium block mb-1">{f.label}</label>
              <input className="field" value={config[f.key] || ""} onChange={(e) => setConfig({ ...config, [f.key]: e.target.value })} data-testid={`integ-field-${f.key}`} />
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="btn btn-ghost" data-testid="integ-connect-cancel">{t("os.integ.cancel")}</button>
          <button onClick={submit} disabled={saving} className="btn btn-primary" data-testid="integ-connect-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("os.integ.connect")}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export default function Integraciones() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectItem, setConnectItem] = useState(null);
  const [deliveries, setDeliveries] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get("/integrations")).data); } catch { /* */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const disconnect = async (p) => { await api.post(`/integrations/${p}/disconnect`); toast.success(t("os.integ.disconnectedToast")); load(); };
  const test = async (p) => {
    try { const r = await api.post(`/integrations/${p}/test`); toast.success(`${t("os.integ.testedToast")} · ${r.data.delivery?.code || "-"}`); loadDeliveries(p); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const loadDeliveries = async (p) => { const r = await api.get(`/integrations/${p}/deliveries`); setDeliveries((prev) => ({ ...prev, [p]: r.data })); };

  return (
    <OSLayout title="Integraciones">
      <div className="space-y-8" data-testid="integraciones-page">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
          <div className="overline text-navy/40 mb-2 flex items-center gap-2"><Zap className="w-3.5 h-3.5" /> {t("os.integ.overline")}</div>
          <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">{t("os.integ.title")}</h2>
          <p className="mt-2 text-navy/50 max-w-2xl">{t("os.integ.subtitle")}</p>
        </motion.div>

        {loading ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
          : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="integ-grid">
              {items.map((it) => {
                const Icon = ICONS[it.provider] || Zap;
                return (
                  <div key={it.provider} className={`card-surface p-5 flex flex-col ${!it.available ? "opacity-60" : ""}`} data-testid={`integ-${it.provider}`}>
                    <div className="flex items-start justify-between">
                      <div className="w-11 h-11 rounded-lg bg-violet/10 flex items-center justify-center text-violet"><Icon className="w-5 h-5" /></div>
                      {it.connected ? <span className="badge badge-emerald" data-testid={`integ-connected-${it.provider}`}>{t("os.integ.connected")}</span>
                        : !it.available ? <span className="badge badge-muted">{t("os.integ.soon")}</span> : null}
                    </div>
                    <h3 className="font-medium mt-3">{it.name}</h3>
                    <p className="text-sm text-navy/50 mt-1 flex-1">{it.description}</p>
                    <div className="mt-4 flex items-center gap-2">
                      {!it.available ? null : it.connected ? (
                        <>
                          <button onClick={() => test(it.provider)} className="btn btn-outline text-xs" data-testid={`integ-test-${it.provider}`}><PlayCircle className="w-3.5 h-3.5" /> {t("os.integ.test")}</button>
                          <button onClick={() => disconnect(it.provider)} className="btn btn-ghost text-xs" data-testid={`integ-disconnect-${it.provider}`}>{t("os.integ.disconnect")}</button>
                        </>
                      ) : (
                        <button onClick={() => setConnectItem(it)} className="btn btn-primary text-sm" data-testid={`integ-connect-${it.provider}`}>{t("os.integ.connect")}</button>
                      )}
                    </div>
                    {it.connected && deliveries[it.provider] && (
                      <div className="mt-3 border-t border-navy/10 pt-2 space-y-1">
                        {deliveries[it.provider].length === 0 ? <div className="text-[11px] text-navy/40">{t("os.integ.noDeliveries")}</div>
                          : deliveries[it.provider].slice(0, 5).map((d) => (
                            <div key={d.id} className="flex items-center gap-2 text-[11px]">
                              {d.status === "ok" ? <Check className="w-3 h-3 text-emerald-600" /> : <AlertTriangle className="w-3 h-3 text-destructive" />}
                              <span className="font-mono text-navy/40">{d.code || "—"}</span><span className="text-navy/60">{d.event}</span>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
      </div>
      {connectItem && <ConnectDialog item={connectItem} onClose={() => setConnectItem(null)} onDone={load} t={t} />}
    </OSLayout>
  );
}
